import { TFunction } from 'i18next';
import { ReactNode } from 'react';
import { GenericResponse } from 'src/js/model/api/response/generic-response';
import { constant, PRODUCT } from 'src/js/constant';
import * as yup from 'yup';

export enum LogoPosition {
  LOGO_POS_BOTTOM_RIGHT = 0,
  LOGO_POS_TOP_RIGHT,
  LOGO_POS_BOTTOM_LEFT,
  LOGO_POS_TOP_LEFT,
  LOGO_POS_CENTERED,
  LOGO_POS_ABSOLUTE,
  LOGO_POS_RELATIVE,
}

export interface LogoModel {
  id: string | number;
  name: string;
  thumbnail: string | ReactNode;
  resolution: string;
  mtime: number;
}

export interface LogoSettings {
  interface: number /* model unique id */;
  display: boolean;
  if_name: string;
  opacity: number;
  scaling: number;
  relative: boolean;
  positioning: LogoPosition;
  x: number;
  y: number;
  fileName: string;
  fileNameUrlEncoded: string; // same as fileName above but urlencoded with RFC-3986
}

export interface LogoSettingsViewModel extends LogoSettings {
  panelColor?: string;
  url?: string;
}

export interface GetAllLogosResponse extends GenericResponse {
  data: LogoModel[];
}

export interface GetAllLogoSettingsResponse extends GenericResponse {
  data: LogoSettings[];
}

export interface GetLogoSettingsResponse extends GenericResponse {
  data: LogoSettings;
}

export const logoSettingsSchema = (t: TFunction) =>
  yup.object().shape({
    scaling: yup.number().min(25).max(400),
    opacity: yup.number().min(0).max(100), // In theory min is 1 but in case the CLI configured it at 0
    x: yup
      .number()
      .when('positioning', {
        is: LogoPosition.LOGO_POS_RELATIVE,
        then: yup
          .number()
          .min(0, t('logos.validations.x_rel'))
          .max(1920, constant.product === PRODUCT.FX ? t('logos.validations.fx.x_rel') : t('logos.validations.x_rel')),
      })
      .when('positioning', {
        is: LogoPosition.LOGO_POS_ABSOLUTE,
        then: yup
          .number()
          .min(0, t('logos.validations.x_abs'))
          .max(
            constant.product === PRODUCT.FX ? 4095 : 3839,
            constant.product === PRODUCT.FX ? t('logos.validations.fx.x_abs') : t('logos.validations.x_abs'),
          ),
      }),
    y: yup
      .number()
      .when('positioning', {
        is: LogoPosition.LOGO_POS_RELATIVE,
        then: yup
          .number()
          .min(0, t('logos.validations.y_rel'))
          .max(1080, constant.product === PRODUCT.FX ? t('logos.validations.fx.y_rel') : t('logos.validations.y_rel')),
      })
      .when('positioning', {
        is: LogoPosition.LOGO_POS_ABSOLUTE,
        then: yup
          .number()
          .min(0, t('logos.validations.y_abs'))
          .max(2159, constant.product === PRODUCT.FX ? t('logos.validations.fx.y_abs') : t('logos.validations.y_abs')),
      }),
  });

export const logoPositionToIcon = (pos: LogoPosition) => {
  if (pos === LogoPosition.LOGO_POS_ABSOLUTE) {
    return 'PlacementAbsolute';
  }
  if (pos === LogoPosition.LOGO_POS_BOTTOM_LEFT) {
    return 'PlacementBottomLeft';
  }
  if (pos === LogoPosition.LOGO_POS_BOTTOM_RIGHT) {
    return 'PlacementBottomRight';
  }
  if (pos === LogoPosition.LOGO_POS_CENTERED) {
    return 'PlacementCentered';
  }
  if (pos === LogoPosition.LOGO_POS_RELATIVE) {
    return 'PlacementRelative';
  }
  if (pos === LogoPosition.LOGO_POS_TOP_LEFT) {
    return 'PlacementTopLeft';
  }
  if (pos === LogoPosition.LOGO_POS_TOP_RIGHT) {
    return 'PlacementTopRight';
  }
  return 'PlacementBottomRight';
};
